import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import { Box, Typography, MenuItem, makeStyles } from '@material-ui/core';
import { FC, useEffect } from 'react';
import { useEnvironmentsContext } from '../../context/EnvironmentsContext';
import { WbSelectInline, WbSelectInlineItem } from '../WbSelectInline';
import { WbSelect } from '../WbTextField/WbSelect';
import { Severity, WbTooltip } from '../WbTooltip';
import { WbEnvironmentWarning } from '../WbEnvironmentAccessState';
import { useResponsiveMobile } from '../../hooks';

interface Props {
  disabled?: boolean;
  defaultValue?: string;
  onChange?: (e: string) => void;
}

const useStyles = makeStyles(() => {
  return {
    inputStyle: {
      minHeight: 'auto',
      backgroundColor: 'white',
    },
  };
});

export const WbEnvironmentSelector: FC<Props> = ({ disabled, onChange }) => {
  const {
    environmentList,
    environmentAccess,
    environment,
    setEnvironment,
    disabledStatus,
    setDisabledStatus,
  } = useEnvironmentsContext();
  useEffect(
    () => setDisabledStatus(disabled ?? false),
    [disabled, setDisabledStatus],
  );

  const isMobile = useResponsiveMobile();
  const classes = useStyles();

  if (environmentAccess.status === 'empty') {
    return <WbEnvironmentWarning />;
  }

  if (environmentAccess.status !== 'ready' || !environment) {
    return <></>;
  }

  const selector = isMobile ? (
    <WbSelect
      label="Environment"
      id="environment-selector"
      value={environment.name}
      disabled={disabledStatus || environmentList.length === 1}
      onChange={e => {
        const value = e.target.value as string;
        const nextEnvironment = environmentList.find(env => env.name === value);
        if (!nextEnvironment) return;
        setEnvironment(nextEnvironment);
        if (onChange) onChange(value);
      }}
      clearable={false}
      InputProps={{
        classes: {
          root: classes.inputStyle,
        },
      }}
      style={{ minHeight: 'auto' }}
    >
      {environmentList.map(e => (
        <MenuItem key={e.name} value={e.name}>
          {snakeCaseToTitleCase(e.name)}
        </MenuItem>
      ))}
    </WbSelect>
  ) : (
    <WbSelectInline
      title="Environment"
      menuTitle="Environments"
      value={environment.name}
      disabled={disabledStatus || environmentList.length === 1}
      onChange={e => {
        const nextEnvironment = environmentList.find(env => env.name === e);
        if (!nextEnvironment) return;
        setEnvironment(nextEnvironment);
        if (onChange) onChange(e);
      }}
    >
      {environmentList.map(e => (
        <WbSelectInlineItem key={e.name} value={e.name}>
          <Typography variant="body2">
            {snakeCaseToTitleCase(e.name)}
          </Typography>
        </WbSelectInlineItem>
      ))}
    </WbSelectInline>
  );

  if (environmentList.length > 1) return selector;

  return (
    <WbTooltip
      arrow
      severity={Severity.Warning}
      placement="bottom-start"
      title={
        <>
          <Typography style={{ fontWeight: 700 }}>
            There is only one environment set up
          </Typography>
          <Typography>
            You have access to only one environment. Contact your Admin for more
            information.
          </Typography>
        </>
      }
    >
      <Box>{selector}</Box>
    </WbTooltip>
  );
};
